import { type FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ZoomIn } from 'lucide-react';
import { Button } from '@/components/ui';
import { useAuth } from '@/hooks';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';
import type { SpecialEvent } from '@/types';

interface HeroSectionProps {
  event: SpecialEvent;
  isSoldOut?: boolean;
}

/**
 * Hero section for special event page
 * Full-width hero with background image, title, subtitle, and optional CTA button
 * Button only displays if registerButtonText and registerButtonUrl are configured
 */
export const HeroSection: FC<HeroSectionProps> = ({ event, isSoldOut = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasRegisterButton = !REGISTRATIONS_CLOSED && event.registerButtonText && event.registerButtonUrl;
  const isExternalUrl = event.registerButtonUrl?.startsWith('http');
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Only show image if URL exists and hasn't errored
  const showImage = event.heroImageUrl && !imageError;

  return (
    <>
      <section className="w-full bg-turquoise-900 py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className={`flex flex-col ${showImage ? 'md:flex-row' : ''} items-center gap-8`}>
            {/* Portrait Image - only show if URL exists and loads successfully */}
            {showImage && (
              <div className="w-full max-w-sm md:max-w-md flex-shrink-0">
                <button
                  onClick={() => setIsLightboxOpen(true)}
                  className={`relative group cursor-pointer w-full rounded-lg overflow-hidden shadow-2xl ring-2 ring-turquoise-600/50 hover:ring-orange-500/50 transition-all ${!imageLoaded ? 'min-h-[200px] bg-turquoise-800 animate-pulse' : ''}`}
                >
                  <img
                    src={event.heroImageUrl!}
                    alt={event.title}
                    className={`w-full h-auto object-contain transition-all duration-300 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                  />
                  {/* Hover overlay */}
                  {imageLoaded && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3 shadow-lg">
                        <ZoomIn className="h-6 w-6 text-turquoise-800" />
                      </div>
                    </div>
                  )}
                </button>
              </div>
            )}

            {/* Title Content */}
            <div className={`flex-1 ${showImage ? 'text-center md:text-left' : 'text-center'}`}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-viking text-white mb-3 drop-shadow-lg">
                {event.title}
              </h1>

              {event.subtitle && (
                <p className="text-xl md:text-2xl text-orange-300 mb-2">
                  {event.subtitle}
                </p>
              )}

              {event.heroSubtitle && (
                <p className={`text-base md:text-lg text-white/90 ${showImage ? 'max-w-2xl' : 'max-w-3xl mx-auto'}`}>
                  {event.heroSubtitle}
                </p>
              )}

              {hasRegisterButton && !isSoldOut && (
                isExternalUrl ? (
                  <Button
                    size="lg"
                    className="mt-6 text-lg px-8 py-6 font-bold shadow-xl hover:shadow-2xl transition-all"
                    asChild
                  >
                    <a href={event.registerButtonUrl!} target="_blank" rel="noopener noreferrer">
                      {event.registerButtonText}
                    </a>
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    className="mt-6 text-lg px-8 py-6 font-bold shadow-xl hover:shadow-2xl transition-all"
                    onClick={() => {
                      if (!user) {
                        navigate('/login', { state: { returnTo: event.registerButtonUrl! } });
                      } else {
                        navigate(event.registerButtonUrl!);
                      }
                    }}
                  >
                    {event.registerButtonText}
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal - only if image exists and loaded */}
      {isLightboxOpen && showImage && imageLoaded && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Close button */}
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-8 w-8 text-white" />
          </button>

          {/* Large image */}
          <img
            src={event.heroImageUrl!}
            alt={event.title}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};
